import {
  PRESETS,
  SLIDER_MIN,
  SLIDER_MAX,
  SLIDER_STEP,
  PRICE_PER_CREDIT,
  fmtPrice,
} from "./creditsConfig";

interface Props {
  amount: number;
  onChange: (n: number) => void;
  isCompact: boolean;
}

export default function CreditSelector({ amount, onChange, isCompact }: Props) {
  const pct = ((amount - SLIDER_MIN) / (SLIDER_MAX - SLIDER_MIN)) * 100;
  const total = amount * PRICE_PER_CREDIT;

  return (
    <div className="flex flex-col gap-[18px]">
      <div className="grid grid-cols-4 gap-2">
        {PRESETS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            className={`rounded-lg border font-semibold tabular-nums transition-colors ${isCompact ? "py-2.5 text-sm" : "py-3 text-[15px]"} ${
              amount === p
                ? "border-foreground/40 bg-muted text-foreground"
                : "border-border bg-muted/50 text-foreground hover:border-secondary/40 hover:bg-muted"
            }`}
          >
            {p}
          </button>
        ))}
      </div>

      <div className="space-y-2 px-0.5">
        <div className="relative h-7">
          <div className="pointer-events-none absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-foreground/60"
              style={{ width: `${pct}%` }}
            />
          </div>
          <input
            type="range"
            min={SLIDER_MIN}
            max={SLIDER_MAX}
            step={SLIDER_STEP}
            value={amount}
            onChange={(e) => onChange(Number(e.target.value))}
            aria-label="Credit amount"
            className="credits-slider absolute inset-0 z-[2] m-0 size-full cursor-pointer appearance-none bg-transparent p-0"
          />
        </div>
        <div className="flex justify-between text-[11px] tabular-nums text-muted-foreground">
          <span>{SLIDER_MIN}</span>
          <span>{SLIDER_MAX}</span>
        </div>
      </div>

      <div
        className={`flex flex-wrap items-baseline justify-center gap-x-1.5 rounded-lg border border-border/70 bg-muted/40 tabular-nums ${isCompact ? "px-3.5 py-2.5 text-[13px]" : "px-4 py-3 text-[14px]"}`}
      >
        <span className="font-semibold text-foreground">
          {amount.toLocaleString()}
        </span>
        <span className="-ml-1 text-muted-foreground">
          {amount === 1 ? "credit" : "credits"}
        </span>
        <span className="opacity-45" aria-hidden="true">
          ·
        </span>
        <span className="font-semibold text-foreground">{fmtPrice(total)}</span>
      </div>

      {/* Slider thumb styles — not achievable with Tailwind alone */}
      <style>{`
        .credits-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 22px; height: 22px; border-radius: 50%;
          background: hsl(var(--foreground));
          border: 3px solid hsl(var(--secondary));
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          transition: transform 100ms;
        }
        .credits-slider:active::-webkit-slider-thumb { transform: scale(1.1); }
        .credits-slider::-moz-range-thumb {
          width: 22px; height: 22px; border-radius: 50%;
          background: hsl(var(--foreground));
          border: 3px solid hsl(var(--secondary));
          cursor: pointer;
        }
        .credits-slider:focus { outline: none; }
        .credits-slider:focus::-webkit-slider-thumb {
          box-shadow: 0 0 0 4px hsl(var(--secondary) / 0.25);
        }
      `}</style>
    </div>
  );
}
